import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { Spinner } from '@fluentui/react-components';
import { AppLayout } from './components/layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { OffboardingPage } from './pages/OffboardingPage';
import { EmployeeUpdatePage } from './pages/EmployeeUpdatePage';
import { HardwareRequestPage } from './pages/HardwareRequestPage';
import { ApprovalPage } from './pages/ApprovalPage';
import { TeamsPage } from './pages/TeamsPage';
import { AdminPage } from './pages/AdminPage';
import { HistoryPage } from './pages/HistoryPage';
import { HistoryDetailPage } from './pages/HistoryDetailPage';
import { AuditPage } from './pages/AuditPage';
import { QuickCreatePage } from './pages/QuickCreatePage';
import { RemoteRegPage } from './pages/RemoteRegPage';
import { useAuth } from './hooks/useAuth';
import { ROUTES } from './config/constants';

/**
 * Protected route wrapper
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
}

/**
 * Admin-only route wrapper
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();

  return (
    <Routes>
      {/* Public route - Login */}
      <Route
        path={ROUTES.HOME}
        element={isAuthenticated ? <Navigate to={ROUTES.DASHBOARD} replace /> : <LoginPage />}
      />

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
        <Route path={ROUTES.ONBOARDING} element={<OnboardingPage />} />
        <Route path={ROUTES.OFFBOARDING} element={<OffboardingPage />} />
        <Route path={ROUTES.EMPLOYEE_UPDATE} element={<EmployeeUpdatePage />} />
        <Route path={ROUTES.HARDWARE_REQUEST} element={<HardwareRequestPage />} />

        {/* Admin-only routes */}
        <Route
          path={ROUTES.APPROVAL}
          element={
            <AdminRoute>
              <ApprovalPage />
            </AdminRoute>
          }
        />
        <Route
          path={ROUTES.TEAMS}
          element={
            <AdminRoute>
              <TeamsPage />
            </AdminRoute>
          }
        />
        <Route
          path={ROUTES.QUICK_CREATE}
          element={
            <AdminRoute>
              <QuickCreatePage />
            </AdminRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN}
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path={ROUTES.HISTORY_DETAIL}
          element={
            <AdminRoute>
              <HistoryDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path={ROUTES.HISTORY}
          element={
            <AdminRoute>
              <HistoryPage />
            </AdminRoute>
          }
        />
        <Route
          path={ROUTES.REMOTE_REGISTRY}
          element={
            <AdminRoute>
              <RemoteRegPage />
            </AdminRoute>
          }
        />
        <Route
          path={ROUTES.AUDIT}
          element={
            <AdminRoute>
              <AuditPage />
            </AdminRoute>
          }
        />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  );
}
